import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API } from "../lib/api";
import { toEmbedUrl } from "../lib/video";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Loader2, PlayCircle, FileText, CheckCircle2, ChevronLeft, Download } from "lucide-react";

export default function Learn() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [active, setActive] = useState(null);
  const [completed, setCompleted] = useState(() => JSON.parse(localStorage.getItem(`ez-progress-${id}`) || "[]"));

  useEffect(() => {
    api.get(`/courses/${id}`).then((res) => {
      if (!res.data.enrolled) {
        navigate(`/courses/${id}`);
        return;
      }
      setCourse(res.data);
      const first = res.data.modules?.[0]?.lessons?.[0];
      setActive(first || null);
    });
  }, [id, navigate]);

  const markComplete = (lessonId) => {
    const next = completed.includes(lessonId) ? completed : [...completed, lessonId];
    setCompleted(next);
    localStorage.setItem(`ez-progress-${id}`, JSON.stringify(next));
  };

  if (!course || !active)
    return <div className="min-h-screen bg-slate-950 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-teal-500" /></div>;

  const total = course.modules.reduce((s, m) => s + m.lessons.length, 0);
  const pct = Math.round((completed.length / total) * 100);
  const pdfUrl = active.pdf_path ? `${API}/files/${active.pdf_path}` : null;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="border-b border-slate-800 bg-slate-900/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-5 h-14 flex items-center gap-4">
          <Link to={`/courses/${id}`} className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white"><ChevronLeft className="h-4 w-4" /> Back</Link>
          <span className="font-semibold truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{course.title}</span>
          <div className="ml-auto flex items-center gap-2 text-xs text-slate-400">
            <div className="w-28 h-1.5 rounded-full bg-slate-700 overflow-hidden"><div className="h-full bg-teal-500 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
            {pct}%
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto grid lg:grid-cols-[1fr_340px]">
        {/* Player */}
        <div className="p-5 sm:p-8">
          <div data-testid="lesson-video-player" className="aspect-video w-full rounded-2xl overflow-hidden bg-black border border-slate-800">
            {active.video_url ? (
              <iframe src={toEmbedUrl(active.video_url)} title={active.title} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            ) : (
              <div className="w-full h-full grid place-items-center text-slate-500">No video for this lesson</div>
            )}
          </div>

          <div className="mt-6 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>{active.title}</h1>
              <p className="text-sm text-slate-400 mt-1">{active.duration}</p>
            </div>
            <Button onClick={() => markComplete(active.id)} className="bg-emerald-600 hover:bg-emerald-500 text-white shrink-0">
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> {completed.includes(active.id) ? "Completed" : "Mark done"}
            </Button>
          </div>

          {pdfUrl && (
            <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="lesson-pdf-download-button" className="mt-5 inline-flex items-center gap-2 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 px-4 py-3 hover:bg-amber-500/20 transition-colors duration-200">
              <FileText className="h-4 w-4" /> {active.pdf_name || "Lesson Notes.pdf"} <Download className="h-4 w-4" />
            </a>
          )}
        </div>

        {/* Playlist */}
        <aside className="border-l border-slate-800 bg-slate-900/40 p-4 lg:h-[calc(100vh-3.5rem)] lg:overflow-y-auto">
          {course.modules.map((m, mi) => (
            <div key={m.id} className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 px-2 mb-2">Module {mi + 1}: {m.title}</p>
              {m.lessons.map((l) => (
                <button
                  key={l.id}
                  onClick={() => setActive(l)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm transition-colors duration-200 ${
                    active.id === l.id ? "bg-teal-600/20 text-white" : "text-slate-300 hover:bg-slate-800"
                  }`}
                >
                  {completed.includes(l.id) ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" /> : <PlayCircle className="h-4 w-4 text-slate-500 shrink-0" />}
                  <span className="truncate">{l.title}</span>
                  {l.pdf_name && <FileText className="h-3 w-3 text-amber-500 ml-auto shrink-0" />}
                </button>
              ))}
            </div>
          ))}
        </aside>
      </div>
    </div>
  );
}
